// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for managing asset ownership changes.
 * This module provides a form interface for transferring asset ownership
 * between customers and the manufacturer, including support for recording
 * sale prices and effective dates. Handles both assignment to customers
 * and return to manufacturer ownership.
 */
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { Asset } from '@/asset/api/types';
import {
  UnassignAssetParameters,
  unassignAsset,
} from '@/asset/api/unassign-asset';
import { updateAssetOwnership } from '@/asset/api/update-asset-ownership';
import { FormCompanySearch } from '@/components/form-company-search';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { useHandleAPIError } from '@/hooks/use-handle-api-error';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating owner selection.
 * Requires company identification and type information to distinguish
 * between customer assignments and manufacturer returns.
 */
const ownerSchema = z.object(
  { id: z.string(), name: z.string(), isCustomer: z.boolean() },
  { message: 'Owner is required' }
);

/**
 * Form data structure for user input.
 * Includes nullable fields to support progressive form completion
 * and optional sale price information.
 */
interface InputData {
  owner: z.infer<typeof ownerSchema> | null;
  date: DateTime | null;
  salePrice: SerializableCurrency;
}

/**
 * Schema for validating complete form output.
 * Requires owner and date information, with optional sale price.
 * Date validation includes checks to prevent future dates and dates before January 1, 1970.
 */
const outputSchema = z.object({
  owner: ownerSchema,
  date: dateSchema
    .nullable()
    .refine((date): date is DateTime => date !== null, {
      message: 'Effective date is required',
    })
    .refine((date) => date >= MINIMUM_DATE, {
      message: 'Ownership date cannot be before January 1, 1970',
    })
    .refine(
      (selectedDate) => {
        const limit = DateTime.now().endOf('day').plus({ days: 1 });
        return selectedDate <= limit;
      },
      { message: 'Ownership date cannot be in the future' }
    ),
  salePrice: clearableCurrencyInputSchema,
});

type OutputData = z.infer<typeof outputSchema>;

/**
 * Props for configuring the AssetOwnershipDialog component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * Asset to update ownership for.
   * Only requires the key property for operation.
   */
  asset: Pick<Asset, 'key' | 'accounting'>;

  /**
   * Controls the visibility of the dialog.
   * When true, the dialog is displayed to the user.
   */
  isOpen: boolean;

  /**
   * Callback function for dialog closure.
   * Called when the user cancels or completes ownership changes.
   */
  onClose: () => void;
}

/**
 * A dialog component for managing asset ownership changes.
 * Provides a form interface for transferring ownership between
 * customers and the manufacturer, with support for recording
 * sale prices and effective dates.
 *
 * Features:
 * - Company search for owner selection
 * - Date selection for ownership transfer
 * - Optional sale price recording
 * - Automatic handling of customer vs manufacturer transfers
 * - Form validation with error messages
 * - Optimistic updates with error handling
 * - Normalized date handling
 *
 * @param props - Component configuration options
 */
export function AssetOwnershipDialog(props: Props) {
  const queryClient = useQueryClient();

  const assignAssetMutation = useMutation({
    mutationFn: (data: OutputData) => {
      const salePrice = data.salePrice ?? undefined;

      return updateAssetOwnership(props.asset.key, {
        salePrice,
        customerId: data.owner.id,
        date: data.date,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.asset.key] });
      close();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);

      if (
        // Check for ownership date in future error
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_invalid_ownership_date
      ) {
        setError('date', {
          type: 'custom',
          message: 'Ownership date cannot be in the future',
        });
      } else if (
        // Check for duplicate ownership date error
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_duplicate_ownership_date
      ) {
        setError('date', {
          type: 'custom',
          message:
            'An ownership change already exists for this asset on the given date',
        });
      }
    },
  });

  const unassignAssetMutation = useMutation({
    mutationFn: (data: UnassignAssetParameters) =>
      unassignAsset(props.asset.key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.asset.key] });
      close();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);

      // Check for ownership date in future error
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_invalid_ownership_date
      ) {
        setError('date', {
          type: 'custom',
          message: 'Ownership date cannot be in the future',
        });
      }

      // Check for duplicate ownership date error
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_duplicate_ownership_date
      ) {
        setError('date', {
          type: 'custom',
          message:
            'An ownership change already exists for this asset on the given date',
        });
      }
    },
  });

  const { control, reset, setError, handleSubmit } = useForm<
    InputData,
    unknown,
    OutputData
  >({
    resolver: zodResolver(outputSchema),
    defaultValues: {
      owner: null,
      date: null,
      salePrice: toCurrencyInputValue(
        null,
        props.asset.accounting.costCurrencyCode
      ),
    },
  });

  const assignError = useHandleAPIError(assignAssetMutation.error);
  const unassignError = useHandleAPIError(unassignAssetMutation.error);

  const hasOwnershipDateError =
    (assignError !== null &&
      (assignError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_invalid_ownership_date ||
        assignError.apiStatusCode ===
          ERROR_CODE_MAPPING.asset_duplicate_ownership_date)) ||
    (unassignError !== null &&
      (unassignError.apiStatusCode ===
        ERROR_CODE_MAPPING.asset_invalid_ownership_date ||
        unassignError.apiStatusCode ===
          ERROR_CODE_MAPPING.asset_duplicate_ownership_date));

  const isError =
    (assignError !== null || unassignError !== null) && !hasOwnershipDateError;

  const isPending =
    assignAssetMutation.isPending || unassignAssetMutation.isPending;

  /**
   * Resets error states for both mutations.
   * Called when error messages are dismissed.
   */
  const resetErrors = () => {
    assignAssetMutation.reset();
    unassignAssetMutation.reset();
  };

  /**
   * Closes the dialog and resets the form state.
   * Called on successful submission or user cancellation.
   */
  const close = () => {
    props.onClose();
    reset();
  };

  /**
   * Handles form submission after validation.
   * Routes the request to the appropriate mutation based on
   * whether the new owner is a customer or the manufacturer.
   */
  const handleValidSubmission: SubmitHandler<OutputData> = (data) => {
    if (data.owner.isCustomer) {
      return assignAssetMutation.mutate(data);
    }

    unassignAssetMutation.mutate({ date: data.date });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <DialogTitle>Change ownership</DialogTitle>
      <DialogContent dividers>
        <SupportSnackbar
          isOpen={isError}
          message="An error occured while updating the asset ownership."
          onClose={resetErrors}
        />
        <Stack spacing={2}>
          <FormCompanySearch name="owner" label="Owner" control={control} />
          <FormDatePicker
            disableFuture
            name="date"
            label="Effective date"
            control={control}
          />
          <FormCurrencyTextField
            fullWidth
            name="salePrice"
            label="Sale price (optional)"
            control={control}
            lockTo={props.asset.accounting.costCurrencyCode}
            disabledTooltip="A sale is recorded in the asset's own currency"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="small"
          variant="contained"
          disabled={isPending}
        >
          Change
        </Button>
      </DialogActions>
    </Dialog>
  );
}
